import type { MessageEventValue, MessageTemplateInput, TemplateApprovalKey } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export const MESSAGES_ROUTES = { LIST: '/mensagens' } as const;

const ENDPOINT = '/api/v1/messages';

export interface MessageTemplate {
  id: string;
  name: string;
  event: MessageEventValue;
  body: string;
  contentSid: string | null;
  /** Em que pe esta na Meta - so aprovado alcanca quem esta fora das 24h. */
  approval: TemplateApprovalKey;
  /** O que a Meta respondeu ao reprovar. */
  approvalNote: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  /** As marcacoes do texto, na ordem em que a Meta aprovou. */
  variables: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MessageLog {
  id: string;
  appointmentId: string;
  event: MessageEventValue;
  to: string;
  body: string;
  status: 'QUEUED' | 'SENT' | 'FAILED';
  error: string | null;
  createdAt: string;
}

const KEYS = {
  templates: ['message-templates'] as const,
  templatesDoEvento: (event: MessageEventValue) => ['message-templates', event] as const,
  logs: ['message-logs'] as const,
};

export function useMessageTemplates() {
  return useQuery({
    queryKey: KEYS.templates,
    queryFn: () => apiClient.get<{ data: MessageTemplate[] }>(`${ENDPOINT}/templates`),
  });
}

/**
 * Os modelos de um evento so. A lista ja vem filtrada pela empresa de quem
 * esta logado - cada salao aprova os seus na Meta, e um nunca ve o do outro.
 */
export function useMessageTemplatesByEvent(event: MessageEventValue) {
  return useQuery({
    queryKey: KEYS.templatesDoEvento(event),
    queryFn: () =>
      apiClient.get<{ data: MessageTemplate[] }>(`${ENDPOINT}/templates?event=${event}`),
  });
}

export function useMessageLogs() {
  return useQuery({
    queryKey: KEYS.logs,
    queryFn: () => apiClient.get<{ data: MessageLog[] }>(`${ENDPOINT}/logs`),
  });
}

/**
 * Manda o modelo para a Meta analisar. Quem cria o conteudo no provedor e
 * pede a analise e a API - daqui so sai o pedido.
 */
export function useRequestApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<MessageTemplate>(`${ENDPOINT}/templates/${id}/approval`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.templates }),
  });
}

/** Pergunta ao provedor o que a Meta ja respondeu. */
export function useSyncApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<MessageTemplate>(`${ENDPOINT}/templates/${id}/approval/sync`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.templates }),
  });
}

export function useSaveTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: Partial<MessageTemplateInput> }) =>
      id
        ? apiClient.patch<MessageTemplate>(`${ENDPOINT}/templates/${id}`, input)
        : apiClient.post<MessageTemplate>(`${ENDPOINT}/templates`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.templates }),
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`${ENDPOINT}/templates/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.templates }),
  });
}
